import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CheckCircle2,
  Circle,
  ClipboardCheck,
  Loader2,
  Plus,
  RefreshCw,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import {
  VERTRAG_BERATUNG_CHECKLIST,
  type AktenChecklistItemDef,
} from './aktenTemplates';
import {
  addChecklistItemToAkte,
  computeAktenGuardProgressFromChecklist,
  createPortalAkteVertragBeratung,
  findPortalAkteForPath,
  listChecklistItemsForAkte,
  listPortalAktenForCustomer,
  removeChecklistItemFromAkte,
  updateChecklistItemStatus,
  updatePortalAkteNcPath,
} from './portalAkten';
import type { PortalAkteRecord, PortalAktenChecklistRecord } from './portalAktenTypes';
import { portalDocumentRelativeFolder } from './portalDocuments';
import type { PortalProtokoll } from './portalDocuments';

const MIN_RELOAD_INTERVAL_MS = 15_000;

function aktenzeichenLabel(title: string): string {
  const words = title.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return 'Akte';
  if (words.length === 1) return words[0].slice(0, 8);
  return words
    .slice(0, 3)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

function buildChecklistTooltip(
  checklist: PortalAktenChecklistRecord[],
  progress: ReturnType<typeof computeAktenGuardProgressFromChecklist> | null,
): string {
  if (!checklist.length) return 'Keine Checklistenpunkte';
  const lines: string[] = [];
  if (progress) {
  const openRequired = progress.requiredTotal - progress.requiredDone;
    lines.push(
      progress.allRequiredDone
        ? 'Alle Pflichtpunkte erledigt'
        : `${openRequired}/${progress.requiredTotal} Pflicht offen`,
    );
  }
  for (const row of checklist) {
    const done = row.status === 'done' || row.status === 'waived';
    const mark = done ? '✓' : '○';
    const suffix = row.status === 'waived' ? ' (entfällt)' : row.required ? '' : ' (optional)';
    lines.push(`${mark} ${row.label}${suffix}`);
  }
  return lines.join('\n');
}

export interface PortalAktenwaechterPanelProps {
  customerId: string;
  authToken?: string | null;
  /** NC-Ordner der Akte (Editor) oder relativer Dokumenten-Ordner (Hub). */
  aktePath?: string | null;
  /** Im Dokumenten-Hub: alle Akten anzeigen + Auswahl, wenn kein Pfad-Match. */
  showAktenPicker?: boolean;
  /** Dokumente für Drag & Drop-Verknüpfung (Dokumenten-Hub). */
  linkableDocuments?: PortalProtokoll[];
  className?: string;
  completedByEmail?: string;
  embedMode?: 'standalone' | 'strip' | 'hidden';
  onCollapsedSummaryChange?: (summary: string | null) => void;
}

function buildAktenCollapsedSummary(
  akte: PortalAkteRecord | null,
  progress: ReturnType<typeof computeAktenGuardProgressFromChecklist> | null,
  loadedOnce: boolean,
  loading: boolean,
  showCreateForm: boolean,
): string | null {
  if (!loadedOnce && loading) return null;
  if (showCreateForm) return 'Neue Akte — Checkliste wählen';
  if (!akte) {
    return loadedOnce ? 'Keine Akte — anlegen' : null;
  }
  if (!progress) return akte.title;
  const openRequired = progress.requiredTotal - progress.requiredDone;
  if (progress.allRequiredDone) return `${akte.title} · bereit`;
  return `${akte.title} · ${openRequired}/${progress.requiredTotal} Pflicht offen`;
}

export const PortalAktenwaechterPanel: React.FC<PortalAktenwaechterPanelProps> = ({
  customerId,
  authToken,
  aktePath,
  showAktenPicker = false,
  linkableDocuments = [],
  className = '',
  completedByEmail,
  embedMode = 'standalone',
  onCollapsedSummaryChange,
}) => {
  const [akte, setAkte] = useState<PortalAkteRecord | null>(null);
  const [allAkten, setAllAkten] = useState<PortalAkteRecord[]>([]);
  const [checklist, setChecklist] = useState<PortalAktenChecklistRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [busyItemId, setBusyItemId] = useState<string | null>(null);
  const [createBusy, setCreateBusy] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newAkteTitle, setNewAkteTitle] = useState('');
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<Set<string>>(() =>
    new Set(VERTRAG_BERATUNG_CHECKLIST.items.map((i) => i.id)),
  );
  const [newItemLabel, setNewItemLabel] = useState('');
  const [addItemBusy, setAddItemBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [dropActive, setDropActive] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);

  const loadedOnceRef = useRef(false);
  const lastReloadAtRef = useRef(0);
  const authTokenRef = useRef(authToken);
  const aktePathRef = useRef(aktePath);
  authTokenRef.current = authToken;
  aktePathRef.current = aktePath;

  const templateItems = useMemo(
    () => [...VERTRAG_BERATUNG_CHECKLIST.items].sort((a, b) => a.order - b.order),
    [],
  );

  const progress = useMemo(() => {
    if (!checklist.length) return null;
    return computeAktenGuardProgressFromChecklist(checklist);
  }, [checklist]);

  const checklistTooltip = useMemo(
    () => buildChecklistTooltip(checklist, progress),
    [checklist, progress],
  );

  const reloadChecklist = useCallback(
    async (akteId: string, options?: { silent?: boolean }) => {
      const token = authTokenRef.current?.trim() ?? '';
      if (!token || !customerId) return;
      if (!options?.silent && !loadedOnceRef.current) setLoading(true);
      try {
        const rows = await listChecklistItemsForAkte(customerId, token, akteId);
        setChecklist(rows);
      } finally {
        if (!options?.silent && !loadedOnceRef.current) setLoading(false);
      }
    },
    [customerId],
  );

  const reload = useCallback(
    async (options?: { silent?: boolean; force?: boolean; akteIdOverride?: string }) => {
      const token = authTokenRef.current?.trim() ?? '';
      if (!token || !customerId) return;

      const now = Date.now();
      if (!options?.force && now - lastReloadAtRef.current < MIN_RELOAD_INTERVAL_MS) return;
      lastReloadAtRef.current = now;

      const showSpinner = !options?.silent && !loadedOnceRef.current;
      if (showSpinner) setLoading(true);
      try {
        const aktenList = await listPortalAktenForCustomer(customerId, token);
        setAllAkten(aktenList);

        let resolved: PortalAkteRecord | null = null;
        if (options?.akteIdOverride) {
          resolved = aktenList.find((a) => a.id === options.akteIdOverride) ?? null;
        } else {
          resolved = await findPortalAkteForPath(customerId, token, aktePathRef.current);
        }

        setAkte(resolved);
        if (resolved) {
          await reloadChecklist(resolved.id, { silent: true });
        } else {
          setChecklist([]);
        }
        loadedOnceRef.current = true;
        setLoadedOnce(true);
      } finally {
        if (showSpinner) setLoading(false);
      }
    },
    [customerId, reloadChecklist],
  );

  useEffect(() => {
    lastReloadAtRef.current = 0;
    loadedOnceRef.current = false;
    setLoadedOnce(false);
    void reload({ force: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, aktePath]);

  const openCreateForm = useCallback(() => {
    const defaultTitle = aktePathRef.current?.trim()
      ? `Akte ${aktePathRef.current.split('/').filter(Boolean).pop()}`
      : 'Neue Akte Vertrag/Beratung';
    setNewAkteTitle(defaultTitle);
    setSelectedTemplateIds(new Set(VERTRAG_BERATUNG_CHECKLIST.items.map((i) => i.id)));
    setShowCreateForm(true);
    setMessage(null);
  }, []);

  const toggleTemplateItem = useCallback((itemId: string) => {
    setSelectedTemplateIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  }, []);

  const handleCreateAkte = useCallback(async () => {
    const token = authTokenRef.current?.trim() ?? '';
    if (!token) {
      setMessage('Bitte neu anmelden.');
      return;
    }
    const title = newAkteTitle.trim();
    if (!title) {
      setMessage('Bitte einen Akten-Titel eingeben.');
      return;
    }
    if (!selectedTemplateIds.size) {
      setMessage('Mindestens ein Checklisten-Punkt auswählen.');
      return;
    }

    setCreateBusy(true);
    setMessage(null);
    try {
      const ncPath = aktePathRef.current?.trim()
        ? aktePathRef.current.trim().endsWith('/')
          ? aktePathRef.current.trim()
          : `${aktePathRef.current.trim()}/`
        : undefined;
      const result = await createPortalAkteVertragBeratung(customerId, token, {
        title,
        nc_path: ncPath,
        checklist_item_ids: Array.from(selectedTemplateIds),
      });
      if (result.ok === false) {
        setMessage(result.error);
        return;
      }
      setAkte(result.data.akte);
      setAllAkten((prev) => [result.data.akte, ...prev.filter((a) => a.id !== result.data.akte.id)]);
      await reloadChecklist(result.data.akte.id, { silent: true });
      lastReloadAtRef.current = 0;
      void reload({ force: true, akteIdOverride: result.data.akte.id });
      setShowCreateForm(false);
      setMessage(`Akte angelegt — ${result.data.checklistCount} Checklistenpunkte.`);
    } finally {
      setCreateBusy(false);
    }
  }, [customerId, newAkteTitle, reloadChecklist, reload, selectedTemplateIds]);

  const linkDocumentToAkte = useCallback(
    async (doc: PortalProtokoll) => {
      if (!akte) return;
      const token = authTokenRef.current?.trim() ?? '';
      if (!token) return;
      const folder = portalDocumentRelativeFolder(doc.nextcloud_path);
      const linkPath = folder || aktePathRef.current?.trim() || '';
      if (!linkPath) {
        setMessage('Kein Ordnerpfad für die Verknüpfung verfügbar.');
        return;
      }
      setLinkBusy(true);
      setMessage(null);
      try {
        const result = await updatePortalAkteNcPath(token, akte.id, linkPath);
        if (result.ok === false) {
          setMessage(result.error);
          return;
        }
        setAkte(result.data);
        setMessage(`Akte mit Ordner „${linkPath}" verknüpft.`);
      } finally {
        setLinkBusy(false);
      }
    },
    [akte],
  );

  const handleAkteDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDropActive(false);
      if (!akte) return;

      const docId = e.dataTransfer.getData('text/plain')?.trim();
      const doc = docId ? linkableDocuments.find((d) => d.id === docId) : undefined;
      if (doc) {
        await linkDocumentToAkte(doc);
        return;
      }

      const files = Array.from(e.dataTransfer.files ?? []);
      if (files.length) {
        setMessage(
          'Datei hier abgelegt — bitte zuerst unter „Hinzufügen" hochladen; danach per Drag auf die Akte verknüpfen.',
        );
        return;
      }

      setMessage('Ziehen Sie ein Dokument aus der Liste auf die Akte, um den Ordner zu verknüpfen.');
    },
    [akte, linkDocumentToAkte, linkableDocuments],
  );

  const handleToggleDone = useCallback(
    async (row: PortalAktenChecklistRecord) => {
      const token = authTokenRef.current?.trim() ?? '';
      if (!token) return;
      const nextStatus = row.status === 'done' ? 'open' : 'done';
      setBusyItemId(row.id);
      setMessage(null);
      try {
        const result = await updateChecklistItemStatus(token, row.id, nextStatus, completedByEmail);
        if (result.ok === false) {
          setMessage(result.error);
          return;
        }
        setChecklist((prev) => prev.map((item) => (item.id === row.id ? result.data : item)));
      } finally {
        setBusyItemId(null);
      }
    },
    [completedByEmail],
  );

  const handleWaive = useCallback(
    async (row: PortalAktenChecklistRecord) => {
      const token = authTokenRef.current?.trim() ?? '';
      if (!token) return;
      const nextStatus = row.status === 'waived' ? 'open' : 'waived';
      setBusyItemId(row.id);
      setMessage(null);
      try {
        const result = await updateChecklistItemStatus(token, row.id, nextStatus, completedByEmail);
        if (result.ok === false) {
          setMessage(result.error);
          return;
        }
        setChecklist((prev) => prev.map((item) => (item.id === row.id ? result.data : item)));
      } finally {
        setBusyItemId(null);
      }
    },
    [completedByEmail],
  );

  const handleAddItem = useCallback(async () => {
    if (!akte) return;
    const token = authTokenRef.current?.trim() ?? '';
    if (!token) return;
    const label = newItemLabel.trim();
    if (!label) return;

    setAddItemBusy(true);
    setMessage(null);
    try {
      const result = await addChecklistItemToAkte(customerId, token, akte.id, label, false);
      if (result.ok === false) {
        setMessage(result.error);
        return;
      }
      setChecklist((prev) => [...prev, result.data].sort((a, b) => a.sort_order - b.sort_order));
      setNewItemLabel('');
    } finally {
      setAddItemBusy(false);
    }
  }, [akte, customerId, newItemLabel]);

  const handleRemoveItem = useCallback(async (row: PortalAktenChecklistRecord) => {
    if (!row.item_id.startsWith('custom_')) return;
    if (!window.confirm(`Punkt „${row.label}" entfernen?`)) return;
    const token = authTokenRef.current?.trim() ?? '';
    if (!token) return;
    setBusyItemId(row.id);
    setMessage(null);
    try {
      const result = await removeChecklistItemFromAkte(token, row.id);
      if (result.ok === false) {
        setMessage(result.error);
        return;
      }
      setChecklist((prev) => prev.filter((item) => item.id !== row.id));
    } finally {
      setBusyItemId(null);
    }
  }, []);

  const renderTemplateCheckbox = (item: AktenChecklistItemDef) => (
    <label
      key={item.id}
      className="flex items-start gap-2 rounded-md px-2 py-1 hover:bg-white/[0.03] cursor-pointer"
    >
      <input
        type="checkbox"
        checked={selectedTemplateIds.has(item.id)}
        onChange={() => toggleTemplateItem(item.id)}
        className="mt-0.5 rounded border-white/20"
      />
      <span className="min-w-0 flex-1">
        <span className="text-[11px] text-slate-200">{item.label}</span>
        <span className="block text-[10px] text-slate-600">{item.required ? 'Pflicht' : 'Optional'}</span>
      </span>
    </label>
  );

  const collapsedSummary = buildAktenCollapsedSummary(
    akte,
    progress,
    loadedOnce,
    loading,
    showCreateForm,
  );

  useEffect(() => {
    onCollapsedSummaryChange?.(collapsedSummary);
  }, [collapsedSummary, onCollapsedSummaryChange]);

  if (!authToken?.trim()) return null;

  if (embedMode === 'hidden') return null;

  const showCreateCta =
    !akte && loadedOnce && !showCreateForm && (showAktenPicker || Boolean(aktePath?.trim()));
  const showWeitereAkteButton = showAktenPicker && akte && !showCreateForm;
  const showEmptyPathHint = !aktePath?.trim() && !showAktenPicker && !akte && loadedOnce;

  if (showEmptyPathHint) return null;

  const openRequired = progress ? progress.requiredTotal - progress.requiredDone : 0;

  const shellClass =
    embedMode === 'strip'
      ? `overflow-hidden ${className}`
      : `rounded-lg border border-white/[0.08] bg-white/[0.02] overflow-hidden ${className}`;

  return (
    <div
      className={shellClass}
      aria-label="Aktenwächter"
      onDragOver={(e) => {
        if (!akte) return;
        e.preventDefault();
        setDropActive(true);
      }}
      onDragLeave={() => setDropActive(false)}
      onDrop={(e) => void handleAkteDrop(e)}
    >
      {embedMode !== 'strip' ? (
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-white/[0.06]">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 m-0 flex items-center gap-1">
            <ClipboardCheck className="w-3 h-3" aria-hidden />
            Aktenwächter
          </p>
          <div className="flex items-center gap-1">
            {akte && progress ? (
              <span
                title={checklistTooltip}
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold border cursor-help ${
                  progress.allRequiredDone
                    ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                    : 'bg-amber-500/10 text-amber-200 border-amber-500/30'
                }`}
              >
                {aktenzeichenLabel(akte.title)}
                <span className="mx-1 opacity-50">·</span>
                {progress.allRequiredDone
                  ? 'bereit'
                  : `${openRequired}/${progress.requiredTotal} Pflicht offen`}
              </span>
            ) : null}
            {showAktenPicker && allAkten.length > 0 ? (
              <select
                value={akte?.id ?? ''}
                onChange={(e) => {
                  const id = e.target.value;
                  if (!id) {
                    setAkte(null);
                    setChecklist([]);
                    return;
                  }
                  lastReloadAtRef.current = 0;
                  void reload({ force: true, akteIdOverride: id });
                }}
                className="max-w-[10rem] rounded border border-white/[0.08] bg-slate-900/80 px-1.5 py-0.5 text-[10px] text-slate-300"
                aria-label="Akte wählen"
              >
                <option value="">Akte wählen…</option>
                {allAkten.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.title}
                  </option>
                ))}
              </select>
            ) : null}
            <button
              type="button"
              onClick={() => {
                lastReloadAtRef.current = 0;
                void reload({ force: true, akteIdOverride: akte?.id });
              }}
              disabled={loading}
              className="p-0.5 text-slate-500 hover:text-slate-300 disabled:opacity-50"
              title="Aktenwächter neu laden"
              aria-label="Aktenwächter neu laden"
            >
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-end gap-1 px-3 py-1 border-b border-white/[0.06]">
          {akte && progress ? (
            <span
              title={checklistTooltip}
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold border cursor-help ${
                progress.allRequiredDone
                  ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                  : 'bg-amber-500/10 text-amber-200 border-amber-500/30'
              }`}
            >
              {aktenzeichenLabel(akte.title)}
              <span className="mx-1 opacity-50">·</span>
              {progress.allRequiredDone
                ? 'bereit'
                : `${openRequired}/${progress.requiredTotal} Pflicht offen`}
            </span>
          ) : null}
          {showAktenPicker && allAkten.length > 0 ? (
            <select
              value={akte?.id ?? ''}
              onChange={(e) => {
                const id = e.target.value;
                if (!id) {
                  setAkte(null);
                  setChecklist([]);
                  return;
                }
                lastReloadAtRef.current = 0;
                void reload({ force: true, akteIdOverride: id });
              }}
              className="max-w-[10rem] rounded border border-white/[0.08] bg-slate-900/80 px-1.5 py-0.5 text-[10px] text-slate-300"
              aria-label="Akte wählen"
            >
              <option value="">Akte wählen…</option>
              {allAkten.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.title}
                </option>
              ))}
            </select>
          ) : null}
          <button
            type="button"
            onClick={() => {
              lastReloadAtRef.current = 0;
              void reload({ force: true, akteIdOverride: akte?.id });
            }}
            disabled={loading}
            className="p-0.5 text-slate-500 hover:text-slate-300 disabled:opacity-50"
            title="Aktenwächter neu laden"
            aria-label="Aktenwächter neu laden"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          </button>
        </div>
      )}

      {progress ? (
        <div
          className={`px-3 py-2 text-xs border-b border-white/[0.06] ${
            progress.allRequiredDone
              ? 'bg-emerald-500/10 text-emerald-300'
              : 'bg-amber-500/5 text-amber-200/90'
          }`}
        >
          <div className="flex items-center gap-2">
            {progress.allRequiredDone ? (
              <ShieldCheck className="w-4 h-4 flex-shrink-0" aria-hidden />
            ) : (
              <Circle className="w-4 h-4 flex-shrink-0" aria-hidden />
            )}
            <span>
              {progress.allRequiredDone
                ? 'Akte bereit — alle gewählten Pflichtpunkte erledigt'
                : `${progress.requiredDone}/${progress.requiredTotal} Pflicht erledigt`}
              {progress.optionalTotal > 0
                ? ` · ${progress.optionalDone}/${progress.optionalTotal} optional`
                : ''}
            </span>
          </div>
        </div>
      ) : null}

      <div
        className={`px-3 py-2 space-y-2 ${dropActive ? 'bg-violet-500/10 ring-1 ring-violet-400/40 ring-inset' : ''}`}
      >
        {akte && dropActive ? (
          <p className="text-[10px] text-violet-300 m-0">Dokument oder Ordner auf die Akte ablegen…</p>
        ) : akte ? (
          <p className="text-[10px] text-slate-600 m-0">
            Tipp: Dokument aus der Liste auf die Akte ziehen, um den Ordner zu verknüpfen.
          </p>
        ) : null}

        {akte ? (
          <p className="text-[11px] text-slate-400 m-0">
            <span className="text-slate-200 font-medium">{akte.title}</span>
            {akte.mandant_label ? ` · ${akte.mandant_label}` : ''}
            {akte.nc_path ? ` · ${akte.nc_path}` : ''}
          </p>
        ) : null}

        {loading && !loadedOnce ? (
          <p className="text-[11px] text-slate-500 m-0 flex items-center gap-1">
            <Loader2 className="w-3 h-3 animate-spin" /> Lade Checkliste…
          </p>
        ) : null}

        {showCreateCta ? (
          <div className="rounded-lg border border-dashed border-violet-500/30 bg-violet-500/5 px-3 py-3 space-y-2">
            <p className="text-[11px] text-slate-400 m-0">
              Keine Akte für diesen Ordner. Wählen Sie die Checklisten-Punkte für diese Akte.
            </p>
            <button
              type="button"
              onClick={openCreateForm}
              className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-[11px] font-semibold px-3 py-1.5"
            >
              <Plus className="w-3.5 h-3.5" />
              Neue Akte anlegen
            </button>
          </div>
        ) : null}

        {showCreateForm ? (
          <div className="rounded-lg border border-violet-500/25 bg-violet-500/5 px-3 py-3 space-y-3">
            <p className="text-[11px] text-slate-300 m-0 font-medium">Neue Akte — Checkliste wählen</p>
            <input
              type="text"
              value={newAkteTitle}
              onChange={(e) => setNewAkteTitle(e.target.value)}
              placeholder="Akten-Titel…"
              className="w-full rounded border border-white/[0.08] bg-slate-900/70 px-2 py-1.5 text-[11px] text-slate-200"
            />
            <div className="max-h-48 overflow-y-auto space-y-0.5 border border-white/[0.06] rounded-md p-1">
              {templateItems.map(renderTemplateCheckbox)}
            </div>
            <p className="text-[10px] text-slate-600 m-0">
              {selectedTemplateIds.size} von {templateItems.length} Punkten gewählt — nur diese erscheinen in der Akte.
            </p>
            <div className="flex flex-wrap gap-2 pt-1">
              <button
                type="button"
                onClick={() => void handleCreateAkte()}
                disabled={createBusy || !selectedTemplateIds.size}
                className="inline-flex flex-1 min-w-[10rem] items-center justify-center gap-1.5 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white text-xs font-semibold px-4 py-2 shadow-sm"
              >
                {createBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Akte erstellen
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                disabled={createBusy}
                className="text-[11px] text-slate-500 hover:text-slate-300 px-3 py-2"
              >
                Abbrechen
              </button>
            </div>
          </div>
        ) : null}

        {akte && checklist.length === 0 && loadedOnce && !loading ? (
          <p className="text-[11px] text-slate-500 m-0">Keine Checklistenpunkte — bitte aktualisieren.</p>
        ) : null}

        {akte && checklist.length > 0 ? (
          <ul className="m-0 p-0 list-none space-y-1">
            {checklist.map((row) => {
              const isDone = row.status === 'done';
              const isWaived = row.status === 'waived';
              const busy = busyItemId === row.id;
              const isCustom = row.item_id.startsWith('custom_');

              return (
                <li
                  key={row.id}
                  className={`flex items-start gap-2 rounded-md px-2 py-1.5 text-[11px] ${
                    isWaived ? 'opacity-60' : ''
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => void handleToggleDone(row)}
                    disabled={busy || isWaived}
                    className="mt-0.5 flex-shrink-0 text-slate-400 hover:text-emerald-400 disabled:opacity-40"
                    title={isDone ? 'Als offen markieren' : 'Erledigt'}
                    aria-label={isDone ? 'Als offen markieren' : 'Erledigt'}
                  >
                    {busy ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : isDone ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    ) : (
                      <Circle className="w-4 h-4" />
                    )}
                  </button>
                  <div className="min-w-0 flex-1">
                    <p
                      className={`m-0 text-slate-200 ${
                        isDone || isWaived ? 'line-through text-slate-500' : ''
                      }`}
                    >
                      {row.label}
                    </p>
                    <p className="m-0 mt-0.5 text-[10px] text-slate-600">
                      {row.required ? 'Pflicht' : 'Optional'}
                      {isWaived ? ' · entfällt' : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => void handleWaive(row)}
                      disabled={busy}
                      className={`rounded px-1.5 py-0.5 text-[10px] border transition-colors ${
                        isWaived
                          ? 'border-slate-600 text-slate-400'
                          : 'border-white/[0.08] text-slate-500 hover:text-amber-300 hover:border-amber-500/40'
                      }`}
                      title={isWaived ? 'Entfällt zurücknehmen' : 'Entfällt'}
                    >
                      {isWaived ? '↩' : 'entfällt'}
                    </button>
                    {isCustom ? (
                      <button
                        type="button"
                        onClick={() => void handleRemoveItem(row)}
                        disabled={busy}
                        className="p-0.5 text-slate-600 hover:text-red-400 disabled:opacity-40"
                        title="Punkt entfernen"
                        aria-label="Punkt entfernen"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}

        {akte ? (
          <div className="flex flex-col sm:flex-row gap-1.5 pt-1">
            <input
              type="text"
              value={newItemLabel}
              onChange={(e) => setNewItemLabel(e.target.value)}
              placeholder="Eigener Punkt…"
              className="flex-1 min-w-0 rounded border border-white/[0.08] bg-slate-900/70 px-2 py-1 text-[11px] text-slate-200 placeholder:text-slate-600"
            />
            <button
              type="button"
              onClick={() => void handleAddItem()}
              disabled={addItemBusy || !newItemLabel.trim()}
              className="inline-flex items-center justify-center gap-1 rounded border border-white/[0.08] px-2 py-1 text-[10px] text-slate-300 hover:bg-white/[0.04] disabled:opacity-40"
            >
              {addItemBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
              Punkt
            </button>
          </div>
        ) : null}

        {showWeitereAkteButton ? (
          <button
            type="button"
            onClick={openCreateForm}
            disabled={createBusy || linkBusy}
            className="inline-flex items-center gap-1 text-[10px] text-violet-400 hover:text-violet-300 disabled:opacity-50"
          >
            {createBusy || linkBusy ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Plus className="w-3 h-3" />
            )}
            Weitere Akte anlegen
          </button>
        ) : null}

        {message ? <p className="text-[10px] text-amber-300 m-0">{message}</p> : null}
      </div>
    </div>
  );
};
